import {
  ArrowSelect,
  Attach,
  Camera,
  ChatChecked,
  Emo,
  Options,
  Phone,
  Send,
  UserFilled,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/use-auth"
import AppLayout from "@/layouts/app-layout"
import { chatGroupsPath, chatMessagesPath } from "@/routes"

interface ChatMessage {
  id: number
  content: string
  created_at: string
  sender: {
    id: number
    name: string
    avatar_url?: string
  }
  is_read?: boolean
  message_type?: "text" | "image" | "file" | "system"
  attachment_url?: string
}

interface Props {
  conversation: {
    id: number
    title?: string
    participant: {
      id: number
      name: string
      avatar_url?: string
      is_online?: boolean
      last_seen?: string
    }
    messages: ChatMessage[]
    is_group?: boolean
    group_name?: string
    group_members?: {
      id: number
      name: string
      avatar_url?: string
    }[]
  }
}

export default function ChatShow({ conversation }: Props) {
  const { user } = useAuth()
  const [newMessage, setNewMessage] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  if (!user) {
    return <div>Cargando...</div>
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation?.messages])

  useEffect(() => {
    const orig = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = orig
    }
  }, [])

  // Keyboard: Capacitor native events (resize: 'none' means visualViewport doesn't work)
  useEffect(() => {
    if (window.innerWidth >= 768) return

    let showListener: { remove: () => void } | undefined
    let hideListener: { remove: () => void } | undefined
    let cleanupVisualViewport: (() => void) | undefined

    const setup = async () => {
      try {
        const { Keyboard } = await import("@capacitor/keyboard")

        showListener = await Keyboard.addListener(
          "keyboardWillShow",
          (info) => {
            const el = containerRef.current
            if (!el) return
            el.style.height = `${window.innerHeight - info.keyboardHeight}px`
          },
        )

        hideListener = await Keyboard.addListener("keyboardWillHide", () => {
          const el = containerRef.current
          if (!el) return
          el.style.removeProperty("height")
          window.scrollTo(0, 0)
          document.documentElement.scrollTop = 0
          document.body.scrollTop = 0
        })
      } catch {
        // Not in Capacitor — use visualViewport fallback (works in Safari browser)
        const vv = window.visualViewport
        if (!vv) return

        const update = () => {
          const el = containerRef.current
          if (!el) return
          const keyboardOpen = vv.height < window.innerHeight - 100
          if (keyboardOpen) {
            el.style.height = `${vv.height}px`
            el.style.top = `${vv.offsetTop}px`
          } else {
            el.style.removeProperty("height")
            el.style.removeProperty("top")
            window.scrollTo(0, 0)
            document.documentElement.scrollTop = 0
            document.body.scrollTop = 0
          }
        }

        vv.addEventListener("resize", update)
        vv.addEventListener("scroll", update)
        cleanupVisualViewport = () => {
          vv.removeEventListener("resize", update)
          vv.removeEventListener("scroll", update)
        }
      }
    }

    void setup()

    return () => {
      showListener?.remove()
      hideListener?.remove()
      cleanupVisualViewport?.()
    }
  }, [])

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim()) return

    router.post(
      chatMessagesPath(),
      {
        chat_id: conversation.id,
        chat_message: { content: newMessage },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setNewMessage("")
        },
      },
    )
  }

  const formatMessageTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Use same logic as Index.tsx for consistency
  const conversationName =
    conversation.title ??
    conversation.group_name ??
    conversation.participant.name

  return (
    <AppLayout showMobileHeader={false} showMobileNav={false}>
      <Head title={`Chat - ${conversationName}`} />

      {/* Chat view (completely independent) */}
      <div
        ref={containerRef}
        className="bg-background pt-safe pb-safe fixed inset-0 z-50 flex flex-col overflow-hidden md:inset-20 md:left-1/2 md:w-[min(72rem,calc(100%-5rem))] md:-translate-x-1/2"
      >
        {/* Chat Header */}
        <div className="flex flex-shrink-0 items-center justify-between pt-3">
          <div className="flex items-center">
            <Link className="ml-1 w-[36px]" href={chatGroupsPath()}>
              <ArrowSelect className="fill-primary h-auto w-[36px] rotate-90" />
            </Link>
            <Avatar className="h-[38px] w-[38px] border-2 border-white">
              <AvatarImage
                src={conversation.participant.avatar_url ?? undefined}
              />
              <AvatarFallback className="bg-modal p-1">
                <UserFilled className="fill-chart-2 h-full w-full" />
              </AvatarFallback>
            </Avatar>
            <h3 className="text-chart-5 ml-[12px] h-[20px] truncate font-['Roboto_Condensed'] text-[17px] font-medium tracking-[0.17px]">
              {conversationName}
            </h3>
          </div>
          <div className="mr-[12px] flex items-center space-x-[8px]">
            <Phone className="[&_.cls-1]:!stroke-primary h-6 w-6" />
            <Camera className="fill-primary h-5 w-6" />
            <Options className="fill-primary h-6 w-6" />
          </div>
        </div>

        {/* Messages Area */}
        <div className="mt-3 flex-1 space-y-3 overflow-y-auto p-4">
          {conversation.messages.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground text-sm">
                Inicia la conversación
              </p>
            </div>
          ) : (
            conversation.messages.map((message) => {
              const isOwnMessage = (message.sender?.id ?? -1) === user.id
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                >
                  <div className="flex max-w-[75%] flex-col">
                    <div
                      className={`rounded-2xl px-4 pt-2 text-[16px] leading-[25px] font-normal tracking-[0.06px] ${
                        isOwnMessage
                          ? "bg-accent rounded-br-md text-white"
                          : "text-chart-5 bg-card rounded-bl-md"
                      }`}
                    >
                      <div className="overflow-wrap-anywhere break-words">
                        {message.content}
                        <span className="inline-block w-full">
                          <span
                            className={`float-right mt-1 text-[13px] leading-[20px] font-light tracking-[0.65px] ${isOwnMessage ? "text-[#CCCCCC]" : "text-[#888888]"}`}
                          >
                            {formatMessageTime(message.created_at)}
                            {isOwnMessage && (
                              <span className="ml-1 inline-flex -space-x-2">
                                <ChatChecked
                                  className={`h-3.5 w-3.5 ${message.is_read ? "fill-[#42D2FF]" : "fill-[#AAAAAA]"}`}
                                />
                                <ChatChecked
                                  className={`h-3.5 w-3.5 ${message.is_read ? "fill-[#42D2FF]" : "fill-[#AAAAAA]"}`}
                                />
                              </span>
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input - flex-shrink-0 keeps it pinned at bottom */}
        <div className="flex-shrink-0 px-3 pt-3 pb-3">
          <form
            onSubmit={handleSendMessage}
            className="flex items-center gap-2"
          >
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="bg-card h-[44px] w-[44px] flex-shrink-0 rounded-[12px] shadow-[0px_0px_4px_#00000029]"
            >
              <Attach className="fill-primary size-6" />
            </Button>

            <div className="bg-card relative flex h-[44px] flex-1 items-center rounded-[12px]">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="absolute ml-1 h-[30px] w-[30px] flex-shrink-0"
              >
                <Emo className="fill-primary size-5.5" />
              </Button>
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="placeholder:text-muted-foreground ml-[25px] h-full w-full rounded-[12px] border-0 bg-transparent px-4 py-0 text-base font-light tracking-[0.07px] opacity-[0.75] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>

            <Button
              type="submit"
              size="icon"
              disabled={!newMessage.trim()}
              className="bg-primary hover:bg-primary/90 h-[34px] w-[34px] flex-shrink-0 rounded-[12px]"
            >
              <Send className="size-6 fill-white" />
            </Button>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
